import React, { useEffect, useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { Calculator, ReceiptText, ArrowRightLeft } from "lucide-react";
import api, { fmtTL } from "@/lib/api";

const EMPLOYER_PARAMS = {
  2026: {
    year: 2026,
    sgkEmployerRate: 21.75,
    unemploymentEmployerRate: 2,
    totalEmployerRate: 23.75,
    pecMin: 33030,
    pecMax: 297270,
  },
};

const SEVERANCE_PARAMS = {
  2026: [
    {
      start: "2026-01-01",
      end: "2026-06-30",
      ceiling: 64948.77,
    },
    {
      start: "2026-07-01",
      end: "2026-12-31",
      ceiling: 73729.87,
    },
  ],
};

export default function Tools() {
  const [amount, setAmount] = useState("");
  const [rate, setRate] = useState("20");
  const [mode, setMode] = useState("haric");

  const [smmAmount, setSmmAmount] = useState("");
  const [smmMode, setSmmMode] = useState("brut");
  const [smmStopaj, setSmmStopaj] = useState("20");
  const [smmKdv, setSmmKdv] = useState("20");

  const [kiraAmount, setKiraAmount] = useState("");
  const [kiraMode, setKiraMode] = useState("brut");
  const [kiraStopaj, setKiraStopaj] = useState("20");

  const [employerYear, setEmployerYear] = useState("2026");
  const [grossSalary, setGrossSalary] = useState("");

  const [severanceStart, setSeveranceStart] = useState("");
  const [severanceEnd, setSeveranceEnd] = useState("");
  const [severanceGross, setSeveranceGross] = useState("");
  const [activeTool, setActiveTool] = useState("kdv");

  const [rates, setRates] = useState(null);
  useEffect(() => { api.get("/market-rates").then((r) => setRates(r.data)).catch(() => {}); }, []);
  const [fxAmount, setFxAmount] = useState("");
  const [fxUnit, setFxUnit] = useState("try");

  const fxResult = useMemo(() => {
    if (!rates) return null;
    const val = Number(fxAmount) || 0;
    const mid = (r) => (r && r.alis != null && r.satis != null) ? (r.alis + r.satis) / 2 : null;
    const usdMid = mid(rates.usd);
    const eurMid = mid(rates.eur);
    const goldMid = mid(rates.gram_altin);

    let tryAmount = null;
    if (fxUnit === "try") tryAmount = val;
    else if (fxUnit === "usd" && usdMid) tryAmount = val * usdMid;
    else if (fxUnit === "eur" && eurMid) tryAmount = val * eurMid;
    else if (fxUnit === "gold" && goldMid) tryAmount = val * goldMid;

    if (tryAmount == null) return null;
    return {
      try: tryAmount,
      usd: usdMid ? tryAmount / usdMid : null,
      eur: eurMid ? tryAmount / eurMid : null,
      gold: goldMid ? tryAmount / goldMid : null,
    };
  }, [rates, fxAmount, fxUnit]);

  const result = useMemo(() => {
    const tutar = Number(amount) || 0;
    const oran = Number(rate) || 0;

    if (tutar <= 0 || oran < 0) {
      return {
        matrah: 0,
        kdv: 0,
        toplam: 0,
      };
    }

    if (mode === "haric") {
      const kdv = tutar * oran / 100;
      return {
        matrah: tutar,
        kdv,
        toplam: tutar + kdv,
      };
    }

    if (mode === "dahil") {
      const matrah = tutar / (1 + oran / 100);
      return {
        matrah,
        kdv: tutar - matrah,
        toplam: tutar,
      };
    }

    const kdv = tutar * oran / 100;
    return {
      matrah: tutar,
      kdv,
      toplam: tutar + kdv,
    };
  }, [amount, rate, mode]);

  const smmResult = useMemo(() => {
    const tutar = Number(smmAmount) || 0;
    const stopajOrani = (Number(smmStopaj) || 0) / 100;
    const kdvOrani = (Number(smmKdv) || 0) / 100;

    if (tutar <= 0) {
      return {
        brut: 0,
        stopaj: 0,
        kdv: 0,
        net: 0,
      };
    }

    let brut = tutar;

    if (smmMode === "net") {
      const katsayi = 1 + kdvOrani - stopajOrani;
      brut = katsayi > 0 ? tutar / katsayi : 0;
    }

    const stopaj = brut * stopajOrani;
    const kdv = brut * kdvOrani;
    const net = brut + kdv - stopaj;

    return {
      brut,
      stopaj,
      kdv,
      net,
    };
  }, [smmAmount, smmMode, smmStopaj, smmKdv]);

  const kiraResult = useMemo(() => {
    const tutar = Number(kiraAmount) || 0;
    const oran = (Number(kiraStopaj) || 0) / 100;

    if (tutar <= 0) {
      return {
        brut: 0,
        stopaj: 0,
        net: 0,
      };
    }

    let brut = tutar;

    if (kiraMode === "net") {
      const katsayi = 1 - oran;
      brut = katsayi > 0 ? tutar / katsayi : 0;
    }

    const stopaj = brut * oran;
    const net = brut - stopaj;

    return {
      brut,
      stopaj,
      net,
    };
  }, [kiraAmount, kiraMode, kiraStopaj]);

  const employerResult = useMemo(() => {
    const params = EMPLOYER_PARAMS[Number(employerYear)];
    const brut = Number(grossSalary) || 0;

    if (!params || brut <= 0) {
      return {
        primeBase: 0,
        sgkEmployer: 0,
        unemploymentEmployer: 0,
        totalEmployerPremium: 0,
        totalCost: 0,
      };
    }

    const primeBase = Math.min(
      Math.max(brut, params.pecMin),
      params.pecMax
    );

    const sgkEmployer = primeBase * params.sgkEmployerRate / 100;
    const unemploymentEmployer =
      primeBase * params.unemploymentEmployerRate / 100;

    const totalEmployerPremium =
      sgkEmployer + unemploymentEmployer;

    return {
      primeBase,
      sgkEmployer,
      unemploymentEmployer,
      totalEmployerPremium,
      totalCost: brut + totalEmployerPremium,
    };
  }, [employerYear, grossSalary]);

  const severanceResult = useMemo(() => {
    if (!severanceStart || !severanceEnd) {
      return {
        totalDays: 0,
        years: 0,
        months: 0,
        days: 0,
        ceiling: 0,
        severanceBase: 0,
        severancePay: 0,
        noticeWeeks: 0,
        noticePay: 0,
        total: 0,
      };
    }

    const start = new Date(severanceStart + "T00:00:00");
    const end = new Date(severanceEnd + "T00:00:00");
    const gross = Number(severanceGross) || 0;

    if (end < start || gross <= 0) {
      return {
        totalDays: 0,
        years: 0,
        months: 0,
        days: 0,
        ceiling: 0,
        severanceBase: 0,
        severancePay: 0,
        noticeWeeks: 0,
        noticePay: 0,
        total: 0,
      };
    }

    const msPerDay = 1000 * 60 * 60 * 24;
    const totalDays = Math.floor((end - start) / msPerDay) + 1;

    let years = end.getFullYear() - start.getFullYear();
    let months = end.getMonth() - start.getMonth();
    let days = end.getDate() - start.getDate();

    if (days < 0) {
      months--;
      const prevMonth = new Date(end.getFullYear(), end.getMonth(), 0);
      days += prevMonth.getDate();
    }

    if (months < 0) {
      years--;
      months += 12;
    }

    let ceiling = 0;
    const exitDate = severanceEnd;

    for (const periods of Object.values(SEVERANCE_PARAMS)) {
      const found = periods.find(
        (x) => exitDate >= x.start && exitDate <= x.end
      );
      if (found) {
        ceiling = found.ceiling;
        break;
      }
    }

    const severanceBase = ceiling > 0 ? Math.min(gross, ceiling) : gross;

    const severancePay =
      totalDays >= 365
        ? severanceBase * (totalDays / 365)
        : 0;

    let noticeWeeks = 0;

    if (totalDays < 180) {
      noticeWeeks = 2;
    } else if (totalDays < 540) {
      noticeWeeks = 4;
    } else if (totalDays < 1080) {
      noticeWeeks = 6;
    } else {
      noticeWeeks = 8;
    }

    const dailyGross = gross / 30;
    const noticePay = dailyGross * noticeWeeks * 7;

    return {
      totalDays,
      years,
      months,
      days,
      ceiling,
      severanceBase,
      severancePay,
      noticeWeeks,
      noticePay,
      total: severancePay + noticePay,
    };
  }, [severanceStart, severanceEnd, severanceGross]);

  return (
    <div className="space-y-6 fade-in">
      <div>
        <h1 className="font-head text-3xl font-semibold tracking-tight">
          Araçlar
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Mali müşavirlik işlemlerinde sık kullanılan hesaplama araçları.
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3">
        {[
          ["kdv", "KDV", "KDV hesaplama"],
          ["smm", "Serbest Meslek", "Makbuz hesaplama"],
          ["kira", "Kira Stopajı", "Brüt / net kira"],
          ["isveren", "İşveren Maliyeti", "SGK ve toplam maliyet"],
          ["kidem", "Kıdem & İhbar", "Tazminat hesaplama"],
          ["doviz", "Döviz & Altın", "USD/EUR/Gram Altın çevirici"],
        ].map(([key, title, desc]) => (
          <button
            key={key}
            type="button"
            onClick={() => setActiveTool(key)}
            className={`text-left rounded-xl border p-4 transition-all ${
              activeTool === key
                ? "border-primary bg-primary text-primary-foreground shadow-sm"
                : "border-border bg-card hover:bg-accent"
            }`}
          >
            <div className="font-head font-semibold text-sm">{title}</div>
            <div
              className={`text-xs mt-1 ${
                activeTool === key
                  ? "text-primary-foreground/70"
                  : "text-muted-foreground"
              }`}
            >
              {desc}
            </div>
          </button>
        ))}
      </div>

      {activeTool === "kdv" && (
      <Card className="p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-accent grid place-items-center">
            <Calculator size={20} />
          </div>
          <div>
            <h2 className="font-head text-lg font-semibold">KDV Hesaplama</h2>
            <p className="text-sm text-muted-foreground">
              KDV hariç veya KDV dahil tutarlardan matrah ve KDV hesaplayın.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="space-y-2">
            <Label>Tutar</Label>
            <Input
              type="number"
              min="0"
              step="0.01"
              placeholder="0,00"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label>İşlem Türü</Label>
            <Select value={mode} onValueChange={setMode}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="haric">KDV Hariç Tutar</SelectItem>
                <SelectItem value="dahil">KDV Dahil Tutar</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>KDV Oranı</Label>
            <Select value={rate} onValueChange={setRate}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1">%1</SelectItem>
                <SelectItem value="10">%10</SelectItem>
                <SelectItem value="20">%20</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card className="p-4">
            <div className="text-xs text-muted-foreground uppercase">
              Matrah
            </div>
            <div className="font-head text-xl font-semibold mt-1">
              {fmtTL(result.matrah)}
            </div>
          </Card>

          <Card className="p-4">
            <div className="text-xs text-muted-foreground uppercase">
              KDV Tutarı
            </div>
            <div className="font-head text-xl font-semibold mt-1">
              {fmtTL(result.kdv)}
            </div>
          </Card>

          <Card className="p-4">
            <div className="text-xs text-muted-foreground uppercase">
              KDV Dahil Toplam
            </div>
            <div className="font-head text-xl font-semibold mt-1">
              {fmtTL(result.toplam)}
            </div>
          </Card>
        </div>

        <div className="flex items-center gap-2 text-xs text-muted-foreground mt-5">
          <ReceiptText size={14} />
          Hesaplama yalnızca matematiksel KDV ayrıştırma ve ekleme işlemi yapar.
        </div>
      </Card>
      )}

      {activeTool === "smm" && (
      <Card className="p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-accent grid place-items-center">
            <ReceiptText size={20} />
          </div>
          <div>
            <h2 className="font-head text-lg font-semibold">
              Serbest Meslek Makbuzu Hesaplama
            </h2>
            <p className="text-sm text-muted-foreground">
              Brüt veya net tutardan stopaj, KDV ve tahsilat tutarını hesaplayın.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="space-y-2">
            <Label>Hesaplama Tipi</Label>
            <Select value={smmMode} onValueChange={setSmmMode}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="brut">Brütten Nete</SelectItem>
                <SelectItem value="net">Netten Brüte</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>{smmMode === "brut" ? "Brüt Tutar" : "Net Tahsilat"}</Label>
            <Input
              type="number"
              min="0"
              step="0.01"
              placeholder="0,00"
              value={smmAmount}
              onChange={(e) => setSmmAmount(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label>Stopaj Oranı (%)</Label>
            <Input
              type="number"
              min="0"
              step="0.01"
              value={smmStopaj}
              onChange={(e) => setSmmStopaj(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label>KDV Oranı (%)</Label>
            <Input
              type="number"
              min="0"
              step="0.01"
              value={smmKdv}
              onChange={(e) => setSmmKdv(e.target.value)}
            />
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="p-4">
            <div className="text-xs text-muted-foreground uppercase">
              Brüt Ücret
            </div>
            <div className="font-head text-xl font-semibold mt-1">
              {fmtTL(smmResult.brut)}
            </div>
          </Card>

          <Card className="p-4">
            <div className="text-xs text-muted-foreground uppercase">
              Stopaj
            </div>
            <div className="font-head text-xl font-semibold mt-1 text-rose-600 dark:text-rose-400">
              {fmtTL(smmResult.stopaj)}
            </div>
          </Card>

          <Card className="p-4">
            <div className="text-xs text-muted-foreground uppercase">
              KDV
            </div>
            <div className="font-head text-xl font-semibold mt-1">
              {fmtTL(smmResult.kdv)}
            </div>
          </Card>

          <Card className="p-4">
            <div className="text-xs text-muted-foreground uppercase">
              Net Tahsil Edilecek
            </div>
            <div className="font-head text-xl font-semibold mt-1 text-emerald-600 dark:text-emerald-400">
              {fmtTL(smmResult.net)}
            </div>
          </Card>
        </div>

        <div className="text-xs text-muted-foreground mt-5">
          Stopaj ve KDV oranlarını işleme göre değiştirebilirsiniz.
        </div>
      </Card>
      )}

      {activeTool === "kira" && (
      <Card className="p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-accent grid place-items-center">
            <Calculator size={20} />
          </div>
          <div>
            <h2 className="font-head text-lg font-semibold">
              Kira Stopajı Hesaplama
            </h2>
            <p className="text-sm text-muted-foreground">
              Brüt veya net işyeri kirasından stopaj tutarını hesaplayın.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="space-y-2">
            <Label>Hesaplama Tipi</Label>
            <Select value={kiraMode} onValueChange={setKiraMode}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="brut">Brütten Nete</SelectItem>
                <SelectItem value="net">Netten Brüte</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>
              {kiraMode === "brut" ? "Brüt Kira" : "Net Kira"}
            </Label>
            <Input
              type="number"
              min="0"
              step="0.01"
              placeholder="0,00"
              value={kiraAmount}
              onChange={(e) => setKiraAmount(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label>Stopaj Oranı (%)</Label>
            <Input
              type="number"
              min="0"
              max="99.99"
              step="0.01"
              value={kiraStopaj}
              onChange={(e) => setKiraStopaj(e.target.value)}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card className="p-4">
            <div className="text-xs text-muted-foreground uppercase">
              Brüt Kira
            </div>
            <div className="font-head text-xl font-semibold mt-1">
              {fmtTL(kiraResult.brut)}
            </div>
          </Card>

          <Card className="p-4">
            <div className="text-xs text-muted-foreground uppercase">
              Stopaj
            </div>
            <div className="font-head text-xl font-semibold mt-1 text-rose-600 dark:text-rose-400">
              {fmtTL(kiraResult.stopaj)}
            </div>
          </Card>

          <Card className="p-4">
            <div className="text-xs text-muted-foreground uppercase">
              Mal Sahibine Ödenecek
            </div>
            <div className="font-head text-xl font-semibold mt-1 text-emerald-600 dark:text-emerald-400">
              {fmtTL(kiraResult.net)}
            </div>
          </Card>
        </div>

        <div className="text-xs text-muted-foreground mt-5">
          Stopaj oranını işleme göre değiştirebilirsiniz.
        </div>
      </Card>
      )}

      {activeTool === "isveren" && (
      <Card className="p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-accent grid place-items-center">
            <Calculator size={20} />
          </div>
          <div>
            <h2 className="font-head text-lg font-semibold">
              İşveren Maliyeti Hesaplama
            </h2>
            <p className="text-sm text-muted-foreground">
              Brüt ücrete göre SGK işveren payı ve toplam işveren maliyetini hesaplayın.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div className="space-y-2">
            <Label>Yıl</Label>
            <Select value={employerYear} onValueChange={setEmployerYear}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.keys(EMPLOYER_PARAMS).map((year) => (
                  <SelectItem key={year} value={year}>
                    {year}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Brüt Ücret</Label>
            <Input
              type="number"
              min="0"
              step="0.01"
              placeholder="0,00"
              value={grossSalary}
              onChange={(e) => setGrossSalary(e.target.value)}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          <Card className="p-4">
            <div className="text-xs text-muted-foreground uppercase">
              Prime Esas Kazanç
            </div>
            <div className="font-head text-xl font-semibold mt-1">
              {fmtTL(employerResult.primeBase)}
            </div>
          </Card>

          <Card className="p-4">
            <div className="text-xs text-muted-foreground uppercase">
              SGK İşveren Payı
            </div>
            <div className="font-head text-xl font-semibold mt-1">
              {fmtTL(employerResult.sgkEmployer)}
            </div>
          </Card>

          <Card className="p-4">
            <div className="text-xs text-muted-foreground uppercase">
              İşsizlik İşveren Payı
            </div>
            <div className="font-head text-xl font-semibold mt-1">
              {fmtTL(employerResult.unemploymentEmployer)}
            </div>
          </Card>

          <Card className="p-4">
            <div className="text-xs text-muted-foreground uppercase">
              Toplam İşveren Primi
            </div>
            <div className="font-head text-xl font-semibold mt-1">
              {fmtTL(employerResult.totalEmployerPremium)}
            </div>
          </Card>

          <Card className="p-4">
            <div className="text-xs text-muted-foreground uppercase">
              Toplam İşveren Maliyeti
            </div>
            <div className="font-head text-xl font-semibold mt-1 text-emerald-600 dark:text-emerald-400">
              {fmtTL(employerResult.totalCost)}
            </div>
          </Card>
        </div>

        <div className="text-xs text-muted-foreground mt-5">
          Hesaplama seçilen yılın SGK parametrelerine göre yapılır. Teşvik ve özel indirimler dahil değildir.
        </div>
      </Card>
      )}

      {activeTool === "kidem" && (
      <Card className="p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-accent grid place-items-center">
            <Calculator size={20} />
          </div>
          <div>
            <h2 className="font-head text-lg font-semibold">
              Kıdem & İhbar Tazminatı Hesaplama
            </h2>
            <p className="text-sm text-muted-foreground">
              Çalışma süresi ve brüt ücrete göre kıdem ve ihbar tazminatını hesaplayın.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="space-y-2">
            <Label>İşe Giriş Tarihi</Label>
            <Input
              type="date"
              value={severanceStart}
              onChange={(e) => setSeveranceStart(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label>İşten Çıkış Tarihi</Label>
            <Input
              type="date"
              value={severanceEnd}
              onChange={(e) => setSeveranceEnd(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label>Kıdeme Esas Aylık Brüt Ücret</Label>
            <Input
              type="number"
              min="0"
              step="0.01"
              placeholder="0,00"
              value={severanceGross}
              onChange={(e) => setSeveranceGross(e.target.value)}
            />
          </div>
        </div>

        <div className="mb-6 p-4 rounded-lg bg-accent/40">
          <div className="text-xs text-muted-foreground uppercase mb-1">
            Çalışma Süresi
          </div>
          <div className="font-head text-lg font-semibold">
            {severanceResult.years} yıl {severanceResult.months} ay {severanceResult.days} gün
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            Toplam {severanceResult.totalDays} gün
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          <Card className="p-4">
            <div className="text-xs text-muted-foreground uppercase">
              Kıdem Tavanı
            </div>
            <div className="font-head text-xl font-semibold mt-1">
              {fmtTL(severanceResult.ceiling)}
            </div>
          </Card>

          <Card className="p-4">
            <div className="text-xs text-muted-foreground uppercase">
              Kıdeme Esas Ücret
            </div>
            <div className="font-head text-xl font-semibold mt-1">
              {fmtTL(severanceResult.severanceBase)}
            </div>
          </Card>

          <Card className="p-4">
            <div className="text-xs text-muted-foreground uppercase">
              Kıdem Tazminatı
            </div>
            <div className="font-head text-xl font-semibold mt-1">
              {fmtTL(severanceResult.severancePay)}
            </div>
          </Card>

          <Card className="p-4">
            <div className="text-xs text-muted-foreground uppercase">
              İhbar Tazminatı
            </div>
            <div className="font-head text-xl font-semibold mt-1">
              {fmtTL(severanceResult.noticePay)}
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              {severanceResult.noticeWeeks} hafta
            </div>
          </Card>

          <Card className="p-4">
            <div className="text-xs text-muted-foreground uppercase">
              Toplam Brüt Tazminat
            </div>
            <div className="font-head text-xl font-semibold mt-1 text-emerald-600 dark:text-emerald-400">
              {fmtTL(severanceResult.total)}
            </div>
          </Card>
        </div>

        <div className="text-xs text-muted-foreground mt-5">
          Hesaplama bilgilendirme amaçlıdır. Kıdem ve ihbar tazminatına hak kazanma durumu fesih nedenine göre ayrıca değerlendirilmelidir.
        </div>
      </Card>
      )}

      {activeTool === "doviz" && (
      <Card className="p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-accent grid place-items-center">
            <ArrowRightLeft size={20} />
          </div>
          <div>
            <h2 className="font-head text-lg font-semibold">Döviz & Altın Çevirici</h2>
            <p className="text-sm text-muted-foreground">
              TL, Dolar, Euro ve Gram Altın arasında güncel kurla yaklaşık çevirim yapın.
            </p>
          </div>
        </div>

        {!rates ? (
          <p className="text-sm text-muted-foreground">Kur bilgisi yükleniyor...</p>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div className="space-y-2">
                <Label>Tutar</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="0,00"
                  value={fxAmount}
                  onChange={(e) => setFxAmount(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label>Birim</Label>
                <Select value={fxUnit} onValueChange={setFxUnit}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="try">Türk Lirası (₺)</SelectItem>
                    <SelectItem value="usd">Dolar (USD)</SelectItem>
                    <SelectItem value="eur">Euro (EUR)</SelectItem>
                    <SelectItem value="gold">Gram Altın</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {fxResult ? (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <Card className="p-4">
                  <div className="text-xs text-muted-foreground uppercase">Türk Lirası</div>
                  <div className="font-head text-lg font-semibold mt-1">{fmtTL(fxResult.try)}</div>
                </Card>
                <Card className="p-4">
                  <div className="text-xs text-muted-foreground uppercase">Dolar</div>
                  <div className="font-head text-lg font-semibold mt-1">
                    {fxResult.usd != null ? `$${fxResult.usd.toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "—"}
                  </div>
                </Card>
                <Card className="p-4">
                  <div className="text-xs text-muted-foreground uppercase">Euro</div>
                  <div className="font-head text-lg font-semibold mt-1">
                    {fxResult.eur != null ? `€${fxResult.eur.toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "—"}
                  </div>
                </Card>
                <Card className="p-4">
                  <div className="text-xs text-muted-foreground uppercase">Gram Altın</div>
                  <div className="font-head text-lg font-semibold mt-1">
                    {fxResult.gold != null ? `${fxResult.gold.toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 4 })} gr` : "—"}
                  </div>
                </Card>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">Bir tutar girin.</p>
            )}

            <div className="flex items-center gap-2 text-xs text-muted-foreground mt-5">
              <ReceiptText size={14} />
              Alış/satış ortalaması ile hesaplanan yaklaşık değerdir; banka veya kuyumcu işlem kurundan farklı olabilir.
              Kaynak: TCMB (Dolar/Euro), Altınkaynak (Gram Altın).
            </div>
          </>
        )}
      </Card>
      )}
    </div>
  );
}
